// Filename: TestimonialsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';

const TestimonialsPage = () => {
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [userType, setUserType] = useState('');
    const [experience, setExperience] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
        script.async = true;
        document.head.appendChild(script);
        return () => {
            const scripts = document.querySelectorAll('script[src*="recaptcha"]');
            scripts.forEach(script => script.remove());
        };

    }, []);

    const handleStarClick = (starRating) => {
        setRating(starRating);
    };

    const handleStarHover = (starRating) => {
        setHoveredRating(starRating);
    };

    const handleStarLeave = () => {
        setHoveredRating(0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!rating || !userType || !experience.trim() || !name.trim() || !email.trim()) {
            alert('Please fill in all fields');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('');

        try {
            const token = await executeRecaptcha();

            const response = await fetch('/api/submit-testimonial', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    rating,
                    userType,
                    experience,
                    name,
                    email,
                    recaptchaToken: token,
                }),
            });

            if (response.ok) {
                setSubmitStatus('success');
                setRating(0);
                setUserType('');
                setExperience('');
                setName('');
                setEmail('');
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error submitting testimonial:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setSubmitStatus(''), 5000);
        }
    };

    const executeRecaptcha = () => {
        return new Promise((resolve, reject) => {
            const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
            if (!window.grecaptcha || !siteKey) {
                return reject('reCAPTCHA not available or missing site key');
            }

            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(siteKey, { action: 'submit_testimonial' })
                    .then(resolve)
                    .catch(reject);
            });
        });
    };

    const displayRating = hoveredRating || rating;

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/appsytec-logo.png"
                            alt="Appsytec Logo"
                            className="h-10 w-auto"
                        />
                    </Link>
                    <Link to="/" className="text-gray-700 font-medium hover:text-red-500 transition-colors">
                        Home
                    </Link>
                </div>
            </nav>

            <main className="flex-grow pt-24 pb-20">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Share Your Experience</h1>
                        <p className="text-xl text-gray-600">
                            We value your feedback and would love to hear about your experience with Appsytec
                        </p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Contact Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your full name"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your.email@example.com"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Star Rating */}
                            <div className="text-center">
                                <label className="block text-lg font-medium text-gray-700 mb-4">
                                    How would you rate your experience? *
                                </label>
                                <div className="flex justify-center space-x-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => handleStarClick(star)}
                                            onMouseEnter={() => handleStarHover(star)}
                                            onMouseLeave={handleStarLeave}
                                            className="focus:outline-none transition-all duration-150"
                                        >
                                            <Star
                                                size={40}
                                                className={`${star <= displayRating
                                                    ? 'fill-red-500 text-red-500'
                                                    : 'text-gray-300 hover:text-red-200'
                                                    } transition-colors duration-150`}
                                            />
                                        </button>
                                    ))}
                                </div>
                                {rating > 0 && (
                                    <p className="text-gray-600 mt-2">
                                        You rated: {rating} star{rating !== 1 ? 's' : ''}
                                    </p>
                                )}
                            </div>

                            {/* User Type Selection */}
                            <div>
                                <label className="block text-lg font-medium text-gray-700 mb-4">
                                    Are you a client or candidate? *
                                </label>
                                <div className="flex justify-center space-x-8">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="client"
                                            checked={userType === 'client'}
                                            onChange={(e) => setUserType(e.target.value)}
                                            className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500"
                                        />
                                        <span className="ml-2 text-gray-700">Client</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userType"
                                            value="candidate"
                                            checked={userType === 'candidate'}
                                            onChange={(e) => setUserType(e.target.value)}
                                            className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500"
                                        />
                                        <span className="ml-2 text-gray-700">Candidate</span>
                                    </label>
                                </div>
                            </div>

                            {/* Experience Input */}
                            <div>
                                <label className="block text-lg font-medium text-gray-700 mb-4">
                                    Tell us about your experience *
                                </label>
                                <textarea
                                    value={experience}
                                    onChange={(e) => setExperience(e.target.value)}
                                    rows={6}
                                    placeholder="Share your thoughts about working with Appsytec..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-colors"
                                    required
                                />
                                <p className="text-sm text-gray-500 italic mt-2">
                                    Note: Your feedback may be featured on our website and marketing materials.
                                    We'll only use your first name and initial of your last name if we do.
                                </p>
                            </div>

                            {/* Submit Button */}
                            <div className="text-center">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !rating || !userType || !experience.trim() || !name.trim() || !email.trim()}
                                    className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed text-white px-8 py-3 rounded-full text-lg font-semibold transition-colors duration-200"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                                </button>
                            </div>

                            {/* reCAPTCHA notice */}
                            <p className="text-xs text-gray-500 text-center">
                                This form is protected by reCAPTCHA and the Google{' '}
                                <a href="https://policies.google.com/privacy" className="text-red-500 hover:underline" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </a>
                                {' '}and{' '}
                                <a href="https://policies.google.com/terms" className="text-red-500 hover:underline" target="_blank" rel="noopener noreferrer">
                                    Terms of Service
                                </a>
                                {' '}apply.
                            </p>

                            {/* Status Messages */}
                            {submitStatus === 'success' && (
                                <div className="text-green-600 text-center bg-green-50 p-4 rounded-lg">
                                    Thank you for your feedback! We truly appreciate you taking the time to share your experience.
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="text-red-600 text-center bg-red-50 p-4 rounded-lg">
                                    Sorry, there was an error submitting your feedback. Please try again or contact us directly.
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                            <span className="ml-2 text-xl font-bold">Appsytec Pty Ltd</span>
                        </div>
                        <p className="text-gray-400 mb-4">© 2025 Appsytec Pty Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default TestimonialsPage;